import { useState, useEffect, useMemo } from 'react';

import { getInsightUserAccess } from 'api/generated/user-insight-access/user-insight-access';
import type { InsightUserDto } from 'api/generated/schemas/insightUserDto';

import { useCurrentUser } from 'hooks/api/use-current-user';

export type InsightActivityUser = InsightUserDto;

interface UseUserActivityArgs {
  insightId: string | number | undefined;
}

interface UseUserActivityResult {
  users: InsightActivityUser[];
  loading: boolean;
  error: unknown;
  totalUsers: number;
  viewedCount: number;
  downloadedCount: number;
}

export const useUserActivity = ({
  insightId,
}: UseUserActivityArgs): UseUserActivityResult => {
  const [users, setUsers] = useState<InsightActivityUser[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<unknown>(null);

  const currentUser = useCurrentUser();
  const userId = currentUser?.data?.oktaUserId;

  useEffect(() => {
    const fetchUserActivity = async (): Promise<void> => {
      if (!userId || !insightId) {
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const list = await getInsightUserAccess(userId, Number(insightId), {
          page: 0,
          size: 100,
        });

        setUsers(list ?? []);
      } catch (err) {
        console.error('Error fetching user activity:', err);
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchUserActivity();
  }, [userId, insightId]);

  const statistics = useMemo(() => {
    const totalUsers = users.length;
    const viewedCount = users.filter((user) => user.viewed).length;
    const downloadedCount = users.filter((user) => user.downloaded).length;

    return {
      totalUsers,
      viewedCount,
      downloadedCount,
    };
  }, [users]);

  return {
    users,
    loading,
    error,
    ...statistics,
  };
};
